import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';
import {
  DEFAULT_ORDER_FIELD_CONTEXT,
  FieldDestinations,
  getFieldDestinations,
  OrderField,
  OrderFieldContext,
} from './order-field-destinations';

/**
 * Provided by OrderCreateComponent so every field marker inside the order form
 * reacts to the selected order type and the "include in order" checkboxes.
 */
@Injectable()
export class OrderFieldDestinationService {
  private readonly context$ = new BehaviorSubject<OrderFieldContext>(
    DEFAULT_ORDER_FIELD_CONTEXT,
  );

  connect(orderForm: FormGroup): Subscription {
    return orderForm.valueChanges
      .pipe(
        startWith(orderForm.value),
        // getRawValue: orderType is disabled when editing an existing order
        map(() => this.readContext(orderForm.getRawValue())),
        distinctUntilChanged(sameContext),
      )
      .subscribe((context) => this.context$.next(context));
  }

  destinations(field: OrderField): Observable<FieldDestinations> {
    return this.context$.pipe(
      map((context) => getFieldDestinations(field, context)),
      distinctUntilChanged(
        (a, b) => a.pdf === b.pdf && a.freeVacancies === b.freeVacancies,
      ),
    );
  }

  private readContext(value): OrderFieldContext {
    return {
      orderType: value?.orderType || DEFAULT_ORDER_FIELD_CONTEXT.orderType,
      displayPlannedTermForProvision:
        !!value?.clientAndContactForm?.displayPlannedTermForProvision,
      passSalaryDescriptionToTemplate:
        !!value?.clientNeedForm?.salaryMatrix?.passDescriptionToTemplate,
      exportFreetextRequirements:
        !!value?.clientAndContactForm?.exportFreetextRequirements,
    };
  }
}

function sameContext(a: OrderFieldContext, b: OrderFieldContext): boolean {
  return (
    a.orderType === b.orderType &&
    a.displayPlannedTermForProvision === b.displayPlannedTermForProvision &&
    a.passSalaryDescriptionToTemplate === b.passSalaryDescriptionToTemplate &&
    a.exportFreetextRequirements === b.exportFreetextRequirements
  );
}
